// The window: glass sidebar from 1024 px; below, the same sidebar as a drawer; below 640 px the iPhone layout with
// the floating tab bar, a search button and the running timer as the accessory above it.
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import markInk from '../../../design/assets/Logos/tellary-mark-ink.svg';
import markWhite from '../../../design/assets/Logos/tellary-mark-white.svg';
import idleInk from '../../../design/assets/Logos/tellary-mark-idle-ink.svg';
import idleWhite from '../../../design/assets/Logos/tellary-mark-idle-white.svg';
import { dayRange } from '../lib/calendar.ts';
import { navigate, useRoute, type Route } from '../lib/router.ts';
import { activeProjects, runningEntry, timerDraftProject } from '../model/data.ts';
import * as m from '../model/mutations.ts';
import { buildSnapshot } from '../model/snapshot.ts';
import { Banner } from '../components/common.tsx';
import { useFmt, useMedia, useNow, useRange, useStore } from '../store/hooks.tsx';
import { Button, cx, GlassPanel, SidebarNav, TabBar, TimerDisplay, type SidebarItem } from '../ui/ds.tsx';
import { Entries } from '../screens/Entries.tsx';
import { Integrations } from '../screens/Integrations.tsx';
import { ProjectPage } from '../screens/ProjectPage.tsx';
import { Pro } from '../screens/Pro.tsx';
import { Projects } from '../screens/Projects.tsx';
import { Reports } from '../screens/Reports.tsx';
import { SessionDetailScreen } from '../screens/SessionDetail.tsx';
import { Sessions } from '../screens/Sessions.tsx';
import { Settings } from '../screens/Settings.tsx';
import { Today } from '../screens/Today.tsx';
import { Sheets } from '../sheets/Sheets.tsx';

export function Wordmark({ running = false }: { running?: boolean }) {
  return (
    <span className="brand" aria-label="Tellary" data-counting={running}>
      <img className="brand__mark brand__mark--ink" src={running ? markInk : idleInk} width={24} height={24} alt="" />
      <img className="brand__mark brand__mark--white" src={running ? markWhite : idleWhite} width={24} height={24} alt="" />
      tellary
    </span>
  );
}

function selectionOf(route: Route): string {
  switch (route.name) {
    case 'session': return 'sessions';
    case 'project': return `project:${route.id}`;
    case 'login': return 'today';
    default: return route.name;
  }
}

function initials(name: string): string {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase() || 'T';
}

function Sidebar({ onNavigate, flat }: { onNavigate: () => void; flat?: boolean }) {
  const store = useStore();
  const t = store.t;
  const fmt = useFmt();
  const route = useRoute();
  const now = useNow(30_000);
  const today = useMemo(() => dayRange(now, store.timeZone), [Math.floor(now / 60_000), store.timeZone]);
  const data = useRange(today).data;
  const snap = data ? buildSnapshot(store.ws, data, today, now) : undefined;
  const running = data ? runningEntry(data, store.me?.workspace?.memberId) : undefined;
  const projects: SidebarItem[] = activeProjects(store.ws).map((p) => {
    const merged = snap?.byProject.get(p.id)?.mergedMs ?? 0;
    return { id: `project:${p.id}`, label: p.name, color: p.colorSlot, count: merged > 0 ? fmt.hm(merged) : undefined };
  });
  const go = (id: string) => {
    onNavigate();
    if (id.startsWith('project:')) navigate({ name: 'project', id: id.slice(8) });
    else navigate(id === 'today' ? '/' : `/${id}`);
  };
  const guest = store.api.guest;
  const liveLine = guest ? t('Nur in diesem Browser')
    : store.live === 'connected' ? t('Live verbunden') : store.live === 'offline' ? t('Offline · Änderungen warten') : t('Verbindet …');
  const who = guest ? t('Gast') : store.memberName;
  return (
    <SidebarNav flat={flat} label={t('Seitenleiste')} value={selectionOf(route)} onChange={go}
      header={<div className="side-brand"><Wordmark running={!!running || (snap?.countingAgents ?? 0) > 0} />{store.api.demo ? <span className="demo-badge">{t('Demo')}</span> : guest ? <span className="demo-badge">{t('Gast')}</span> : null}</div>}
      sections={[
        { items: [
          { id: 'today', icon: 'clock', label: t('Heute'), live: !!running },
          { id: 'entries', icon: 'calendar', label: t('Einträge') },
          { id: 'reports', icon: 'chart', label: t('Berichte') },
        ] },
        { label: t('Agents'), items: [
          { id: 'sessions', icon: 'terminal', label: t('Sessions'), live: (snap?.countingAgents ?? 0) > 0 },
          { id: 'integrations', icon: 'plug', label: t('Integrationen') },
        ] },
        { label: t('Projekte'), items: [{ id: 'projects', icon: 'folder', label: t('Alle Projekte') }, ...projects] },
        { items: [{ id: 'pro', icon: 'check', label: 'Tellary Pro' }] },
      ]}
      footer={
        <div className="side-foot">
          <span className="avatar" aria-hidden="true">{initials(who)}</span>
          <span className="side-foot__who">
            <span className="side-foot__name">{who}</span>
            {!guest ? <Button variant="plain" size="sm" onClick={() => { onNavigate(); navigate({ name: 'settings', tab: 'account' }); }}>{store.me?.workspace?.personal ? t('Privat') : store.me?.workspace?.name}</Button> : null}
            <span className={cx('side-foot__sync', !guest && store.live === 'offline' && 'is-offline')}>{liveLine}</span>
          </span>
          <Button variant="plain" size="sm" iconOnly icon="gear" aria-label={t('Einstellungen')} onClick={() => { onNavigate(); navigate({ name: 'settings', tab: 'general' }); }} />
        </div>
      } />
  );
}

function Screen({ route }: { route: Route }) {
  switch (route.name) {
    case 'today': case 'login': return <Today />;
    case 'entries': return <Entries />;
    case 'reports': return <Reports />;
    case 'sessions': return <Sessions />;
    case 'session': return <SessionDetailScreen key={route.id} id={route.id} />;
    case 'projects': return <Projects />;
    case 'project': return <ProjectPage key={route.id} id={route.id} />;
    case 'integrations': return <Integrations />;
    case 'settings': return <Settings tab={route.tab} />;
    case 'pro': return <Pro />;
    case 'privacy': case 'support': return null; // Public pages are outside the account-dependent shell.
  }
}

function Drawer({ onClose, children }: { onClose: () => void; children: ReactNode }) {
  const t = useStore().t;
  const ref = useRef<HTMLDialogElement>(null);
  useLayoutEffect(() => {
    const dialog = ref.current;
    dialog?.showModal();
    return () => dialog?.close();
  }, []);
  return createPortal(
    <dialog ref={ref} className="drawer" aria-label={t('Navigation')} aria-modal="true"
      onCancel={(e) => { e.preventDefault(); onClose(); }} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="drawer__panel">{children}</div>
    </dialog>, document.body
  );
}

function TopBar({ onMenu, phone }: { onMenu: () => void; phone: boolean }) {
  const store = useStore();
  const t = store.t;
  const now = useNow(30_000);
  const today = useMemo(() => dayRange(now, store.timeZone), [Math.floor(now / 60_000), store.timeZone]);
  const data = useRange(today).data;
  const counting = data ? !!runningEntry(data, store.me?.workspace?.memberId) || buildSnapshot(store.ws, data, today, now).countingAgents > 0 : false;
  return (
    <GlassPanel as="header" shape="panel" flat className="topbar">
      <Button variant="plain" iconOnly icon="menu" aria-label={t('Navigation öffnen')} onClick={onMenu} />
      <Wordmark running={counting} />
      {store.api.demo ? <span className="demo-badge">{t('Demo')}</span> : store.api.guest ? <span className="demo-badge">{t('Gast')}</span> : null}
      <span className="spacer" />
      {!phone ? <Button variant="plain" iconOnly icon="search" aria-label={t('Suchen')} onClick={() => store.openSheet({ kind: 'search' })} /> : null}
      <Button variant="plain" iconOnly icon="gear" aria-label={t('Einstellungen')} onClick={() => navigate({ name: 'settings', tab: 'general' })} />
    </GlassPanel>
  );
}

function tabOf(route: Route): string {
  switch (route.name) {
    case 'projects': case 'project': return 'projects';
    case 'sessions': case 'session': case 'integrations': return 'agents';
    case 'reports': return 'reports';
    case 'settings': case 'pro': return '';
    default: return 'today';
  }
}

/** The phone's floating chrome: the running timer (accessory) above the tab bar and the search button. */
function PhoneBar() {
  const store = useStore();
  const t = store.t;
  const route = useRoute();
  const now = useNow(1000);
  const today = useMemo(() => dayRange(now, store.timeZone), [Math.floor(now / 60_000), store.timeZone]);
  const data = useRange(today).data;
  const running = data ? runningEntry(data, store.me?.workspace?.memberId) : undefined;
  const snap = data ? buildSnapshot(store.ws, data, today, now) : undefined;
  const project = running ? store.ws.projects.find((p) => p.id === running.projectId) : undefined;
  const toggle = () => {
    if (store.timerSettling) return;
    const at = store.clock();
    if (running) void store.apply(m.stopTimer(running, at));
    else {
      const draftProject = timerDraftProject(store.ws, store.ui.draftProjectId);
      // A timer needs a project: without one, Today's timer card shows where to pick it.
      if (!m.canStartTimer(draftProject)) { navigate('/'); return; }
      void store.apply(m.startTimer(store.actor(), undefined, { description: store.ui.draftDescription, project: draftProject }, at));
      store.setUi({ draftDescription: '' });
    }
    store.settleTimer();
  };
  return (
    <div className="phonebar">
      <GlassPanel shape="capsule" className="accessory">
        <span className="accessory__what">
          <span className="accessory__desc">{running ? running.description || t('Ohne Beschreibung') : t('Timer bereit')}</span>
          {project ? <span className="accessory__proj"><span className="tl-dot" style={{ background: `var(--project-${project.colorSlot})` }} />{project.name}</span> : null}
        </span>
        <TimerDisplay ms={running ? now - running.startAt : 0} state={running ? 'running' : 'stopped'} size="sm" />
        <Button variant={running ? 'primary' : 'signal'} iconOnly icon={running ? 'stop' : 'play'} aria-label={running ? t('Timer stoppen') : t('Timer starten')} onClick={toggle} disabled={store.timerSettling} />
      </GlassPanel>
      <div className="phonebar__row">
        <TabBar label={t('Navigation')} value={tabOf(route)} onChange={(id) => navigate(id === 'today' ? '/' : id === 'agents' ? '/sessions' : `/${id}`)} items={[
          { id: 'today', icon: 'clock', label: t('Heute'), live: !!running },
          { id: 'projects', icon: 'folder', label: t('Projekte') },
          { id: 'agents', icon: 'terminal', label: t('Agents'), live: (snap?.countingAgents ?? 0) > 0 },
          { id: 'reports', icon: 'chart', label: t('Berichte') },
        ]} />
        <Button variant="glass" size="lg" iconOnly icon="search" aria-label={t('Suchen')} className="phonebar__search" onClick={() => store.openSheet({ kind: 'search' })} />
      </div>
    </div>
  );
}

export function Shell() {
  const store = useStore();
  const t = store.t;
  const route = useRoute();
  const wide = useMedia('(min-width: 1024px)');
  const phone = useMedia('(max-width: 639.98px)');
  const [drawer, setDrawer] = useState(false);
  useEffect(() => setDrawer(false), [wide]);
  // ⌘K / Ctrl+K opens the search.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        store.openSheet({ kind: 'search' });
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [store]);
  return (
    <div className={cx('shell', wide ? 'shell--wide' : phone ? 'shell--phone' : 'shell--drawer')}>
      <a className="skip" href="#main">{t('Zum Inhalt springen')}</a>
      {wide ? <div className="shell__side"><Sidebar onNavigate={() => {}} /></div> : <TopBar phone={phone} onMenu={() => setDrawer(true)} />}
      {!wide && drawer ? <Drawer onClose={() => setDrawer(false)}><Sidebar flat onNavigate={() => setDrawer(false)} /></Drawer> : null}
      <main className="main" id="main" tabIndex={-1}>
        {store.problem ? (
          <Banner tone="danger" onDismiss={() => store.dismissProblem()} dismissLabel={t('Schließen')}
            actions={<Button size="sm" variant="plain" onClick={() => { store.dismissProblem(); void store.refresh(); }}>{t('Neu laden')}</Button>}>
            {store.problem}
          </Banner>
        ) : null}
        {store.notice ? (
          <Banner tone="neutral" icon="check" onDismiss={() => store.dismissNotice()} dismissLabel={t('Schließen')}>{store.notice}</Banner>
        ) : null}
        {(store.workspaces?.pendingInvites.length ?? 0) > 0 ? <Banner tone="neutral" actions={<Button size="sm" onClick={() => navigate({ name: 'settings', tab: 'account' })}>{t('Einladungen ansehen')}</Button>}>{t('Du hast offene Team-Einladungen.')}</Banner> : null}
        <Screen key={`${store.me?.workspace?.id ?? ''}.${store.me?.workspace?.role ?? ''}`} route={route} />
      </main>
      {phone ? <PhoneBar /> : null}
      <Sheets />
    </div>
  );
}
